import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import FrsDateCell from "./FrsDateCell";
import { useScheduleSave } from "./ScopeLinks";
import { RequestPills } from "./RaScopeRequests";
import { DocChecks } from "./RaDocChecks";
import { MaterialChecks, RiskBadge } from "./RaMaterialChecks";
import { fmtDMY, dateTone, dateTitle, weeksOut, latestOf } from "../lib/raFormat";
import { LEVELS, buildTree, everyKey, idSuffix, nextUnder, nodeRequests, nodeStart, shownLevels, whereLabel } from "../lib/raTree";

const NO_RELATIONS = { groups: [], breaks: [], deps: [], done: [] };
const TOGGLE = "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800 dark:hover:bg-gray-700";
// An opened scope reads as one block: a soft black outline runs around the scope row and its tree rows.
const EDGE = "border-gray-400/70 dark:border-gray-500/70";
const SIDES = "[&>td:first-child]:border-l [&>td:last-child]:border-r [&>td]:border-gray-400/70 dark:[&>td]:border-gray-500/70";

/** "2 Mobs" beside the hours — hover lists them (dates, crew, where). */
function MobsBadge({ mobs, testId }) {
  if (!mobs?.length) return null;
  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <span className="cursor-default text-[11px] font-medium text-sky-700 tabular-nums" data-testid={testId}>
          · {mobs.length} Mob{mobs.length === 1 ? "" : "s"}
        </span>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-72 p-2 text-[11px]" data-testid={`${testId}-list`}>
        <ul className="space-y-1">
          {mobs.map((m) => (
            <li key={m.id} className="flex items-start justify-between gap-2">
              <span className="min-w-0">
                <span className="font-medium text-gray-800">{m.title || (m.punch ? "Punch" : "Mob")}</span>
                {m.where && <span className="block truncate text-gray-500">{m.where}</span>}
              </span>
              <span className="whitespace-nowrap text-right text-gray-600">
                {m.start ? fmtDMY(m.start) : "unscheduled"}{m.end && m.end !== m.start ? `–${fmtDMY(m.end)}` : ""}
                <span className="block text-gray-400">{m.crew || "?"} crew · {Math.round(m.hours || 0)} h · {m.status}</span>
              </span>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

/** Green link = this scope shares its start with the listed scopes (TC ↔ TRA by default). Click to unlink;
 *  grey = not linked, click to link with the default partner when the job has one. */
function LinkMark({ project, sc, scopeRows, actor, onChanged }) {
  const partners = sc.linked || [];
  const partnerDefault = { TC: "TRA", TRA: "TC" }[sc.scope];
  const other = !partners.length && partnerDefault && (scopeRows || []).find((r) => r.scope === partnerDefault);
  if (!partners.length && !other) return null;
  const toggle = async (e) => {
    e.stopPropagation();
    try {
      if (partners.length) {
        await api.post("/schedule/groups/remove", { project_id: project.id, node: { level: "project" }, scope: sc.scope_type, by: actor || "", view: "ra" });
        toast.success(`${sc.scope} unlinked from ${partners.join(", ")} — dates unchanged`);
      } else {
        const { data } = await api.post("/schedule/groups", { project_id: project.id, node: { level: "project" }, scopes: [sc.scope_type, other.scope_type], by: actor || "", view: "ra", use_date: "" });
        if (data.needs_date) {
          const d = Object.values(data.preview.dates).filter(Boolean).sort()[0] || "";
          await api.post("/schedule/groups", { project_id: project.id, node: { level: "project" }, scopes: [sc.scope_type, other.scope_type], by: actor || "", view: "ra", use_date: d });
        }
        toast.success(`${sc.scope} linked with ${other.scope} — one start date`);
      }
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change the link"); }
  };
  return (
    <button type="button" onClick={toggle} data-testid={`project-scope-link-${project.id}-${sc.scope}`} data-linked={partners.length ? "1" : "0"}
            title={partners.length ? `Linked with ${partners.join(", ")} — one start date, move either. Click to unlink.` : `Not linked. Click to link with ${other.scope}.`}
            className={`inline-flex h-4 w-4 items-center justify-center rounded ${partners.length ? "text-emerald-600 hover:bg-emerald-50" : "text-gray-300 hover:text-gray-500"}`}>
      <Link2 className="h-3 w-3" />
    </button>
  );
}

/** The job's FRS rows, locks and open requests — fetched the first time any scope of the job is opened. */
function useRaTree(projectId, wanted) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, lv, rq] = await Promise.all([
        api.get("/frs-items", { params: { project_id: projectId } }),
        api.get("/frs-dates/levels", { params: { project_id: projectId } }),
        api.get(`/projects/${projectId}/scope-requests`, { params: { status: "open" } }).catch(() => ({ data: [] })),
      ]);
      const items = (it.data?.items || it.data || []).filter((i) => !i.is_delta && !i.is_punch);
      const next = { items, levels: lv.data || [], requests: rq.data || [] };
      setData(next);
      return next;
    } catch { toast.error("Could not load this job's rooms"); return null; }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { if (wanted && !data && !loading) load(); }, [wanted]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, reload: load };
}

/** "Building B1" · "Floor 2" · "Area A" · "Room 1105" — the level word in its colour, the name in bold. */
const LevelLabel = ({ level, value, testId }) => {
  const L = LEVELS.find((x) => x.id === level);
  return (
    <span className="inline-flex items-baseline gap-1 whitespace-nowrap" data-testid={testId}>
      <span className={`text-[10px] font-semibold uppercase tracking-wide ${L.text}`}>{L.word}</span>
      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{L.label(value)}</span>
    </span>
  );
};

/** One Building / Floor / Area / Room row — flat (no indent), a level tag says what it is. */
function NodeRow({ project, sc, node, data, isOpen, onToggle, save, onLight, onMob, last }) {
  const sfx = idSuffix(node.key);
  const st = nodeStart(data.levels, sc.scope_type, node);
  const wk = weeksOut(st.date);
  const end = latestOf(node.rows.filter((r) => Number(r.percent_complete || 0) < 1).map((r) => r.eff_abs || ""));
  const where = whereLabel(node.level, node.coords);
  const req = nodeRequests(node, sc, data.requests);
  const nodeSc = { ...sc, ...req, node: { level: node.level, ...node.coords }, node_key: sfx, where, mobs: undefined, request_state: req.request_state };
  const nextWhere = nextUnder(node, st.date);
  const spec = { level: node.level, scope_type: sc.scope_type, ...node.coords };
  const label = <LevelLabel level={node.level} value={node.level === "room" ? (node.value || node.coords.room_type) : node.value} testId={`ra-node-label-${project.id}-${sc.scope}-${sfx}`} />;
  return (
    <tr className={`border-t border-gray-100 dark:border-gray-800 ${SIDES} ${last ? `border-b ${EDGE}` : ""}`}
        data-testid={`ra-node-${project.id}-${sc.scope}-${sfx}`} data-level={node.level}>
      <td></td>
      <td className="px-2 py-1"></td>
      <td className="px-3 py-1">
        <div className="flex items-center gap-2">
          {node.children ? (
            <button type="button" onClick={onToggle} data-testid={`ra-node-toggle-${project.id}-${sc.scope}-${sfx}`} data-open={isOpen ? "1" : "0"}
                    title={isOpen ? "Collapse" : "Expand"} className="inline-flex items-center gap-1 rounded px-0.5 hover:bg-gray-100 dark:hover:bg-gray-800">
              <span className={TOGGLE}>{isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
              {label}
            </button>
          ) : <><span className="inline-block w-5 shrink-0" />{label}</>}
          {node.level === "room"
            ? (node.value && node.coords.room_type && <span className="text-[11px] text-gray-500">{node.coords.room_type}</span>)
            : <span className="text-[11px] tabular-nums text-gray-500" data-testid={`ra-node-rooms-${project.id}-${sc.scope}-${sfx}`}>{node.rooms} room{node.rooms === 1 ? "" : "s"}</span>}
        </div>
      </td>
      <td className="px-3 py-1">
        <RequestPills project={project} sc={nodeSc} onChanged={onLight} onMob={() => onMob(nodeSc)} />
      </td>
      <td colSpan={2}></td>
      <td className="px-3 py-1 text-[11px] text-gray-700" data-testid={`ra-node-where-${project.id}-${sc.scope}-${sfx}`}>
        {nextWhere || <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-1">
        <FrsDateCell value={st.date || ""} locked={st.locked} past={st.past} multi={st.multi} plain
                     onCommit={(v) => save(spec, "start", v)} testId={`ra-node-start-${project.id}-${sc.scope}-${sfx}`}
                     unlockLabel="Back to inherited"
                     hint={st.locked ? `Set here — ${where} and everything under it that is not set on its own`
                       : st.multi ? `Several dates sit below${st.date ? ` — showing ${fmtDMY(st.date)}, the next one coming up` : ""}. Pick one here to align them.`
                       : "Inherited — pick a date to set it at this level"} />
      </td>
      <td className={`px-3 py-1 text-[11px] tabular-nums whitespace-nowrap ${wk.tone}`} data-testid={`ra-node-weeks-${project.id}-${sc.scope}-${sfx}`}>{wk.text || <span className="text-gray-400">—</span>}</td>
      <td className="px-3 py-1">
        <span className={`text-xs tabular-nums ${dateTone(end) || "text-gray-700"}`} data-testid={`ra-node-completion-${project.id}-${sc.scope}-${sfx}`}
              title={end ? `Completion ${dateTitle(end)} · calculated, last room here to finish` : ""}>{fmtDMY(end) || "—"}</span>
      </td>
      <td colSpan={4}></td>
    </tr>
  );
}

/** Every scope row of one job plus, under an opened scope, its Building → Floor → Area → Room rows. */
export default function RaScopeRows({ project, scopeRows, actor, onChanged, onLight, onMob, treeOpen, setTreeOpen }) {
  const pid = project.id;
  const keyOf = (sc, node) => `${pid}|${sc.scope}${node ? `|${node.key}` : ""}`;
  const anyScopeOpen = scopeRows.some((sc) => treeOpen[keyOf(sc)]);
  const { data, loading, reload } = useRaTree(pid, anyScopeOpen);
  const afterChange = async () => { await Promise.all([reload(), onLight?.()]); };
  const { save, dialogs } = useScheduleSave({ projectId: pid, actor, view: "ra", rel: NO_RELATIONS, onChanged: afterChange });

  const trees = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const sc of scopeRows) {
      const rows = data.items.filter((i) => (i.scope_code || i.scope_type) === sc.scope && Number(i.percent_complete || 0) < 1);
      out[sc.scope] = buildTree(rows, shownLevels(rows));
    }
    return out;
  }, [data, scopeRows]);

  // Per scope: every node key under it (needs the rooms loaded).
  const scopeKeys = (sc, d) => {
    const rows = (d?.items || []).filter((i) => (i.scope_code || i.scope_type) === sc.scope && Number(i.percent_complete || 0) < 1);
    return [keyOf(sc), ...everyKey(buildTree(rows, shownLevels(rows))).map((k) => `${pid}|${sc.scope}|${k}`)];
  };
  const scopeAllOpen = (sc) => !!data && treeOpen[keyOf(sc)] && everyKey(trees[sc.scope] || []).every((k) => treeOpen[`${pid}|${sc.scope}|${k}`]);
  // The scope's double chevron: everything under the scope opens down to the rooms, or the whole scope closes.
  const toggleScopeAll = async (sc) => {
    if (scopeAllOpen(sc)) { setTreeOpen((c) => Object.fromEntries(Object.entries(c).filter(([k]) => k !== keyOf(sc) && !k.startsWith(`${keyOf(sc)}|`)))); return; }
    const d = data || await reload();
    if (!d) return;
    setTreeOpen((c) => ({ ...c, ...Object.fromEntries(scopeKeys(sc, d).map((k) => [k, true])) }));
  };
  const toggle = (k) => setTreeOpen((c) => ({ ...c, [k]: !c[k] }));

  const flatten = (sc, nodes) => nodes.flatMap((node) => {
    const k = keyOf(sc, node);
    return [{ k, node }, ...(treeOpen[k] && node.children ? flatten(sc, node.children) : [])];
  });
  const renderNodes = (sc, nodes) => {
    const flat = flatten(sc, nodes);
    return flat.map(({ k, node }, i) => (
      <NodeRow key={k} project={project} sc={sc} node={node} data={data} isOpen={!!treeOpen[k]} onToggle={() => toggle(k)}
               save={save} onLight={afterChange} onMob={onMob} last={i === flat.length - 1} />
    ));
  };

  return (
    <>
      {scopeRows.map((sc) => {
        const start = sc.next_start || {};
        const done = sc.rooms && sc.complete_rooms >= sc.rooms;
        const wk = weeksOut(start.date);
        const open = !!treeOpen[keyOf(sc)];
        const nodes = trees[sc.scope];
        return (
          <React.Fragment key={`${pid}-${sc.scope}`}>
            <tr className={`border-t bg-gray-50/40 dark:bg-gray-900/40 ${open ? `${EDGE} ${SIDES}` : "border-gray-100 dark:border-gray-800"}`}
                data-testid={`project-scope-${pid}-${sc.scope}`} data-open={open ? "1" : "0"}>
              <td className="px-2 py-1.5"></td>
              <td className="px-2 py-1.5"></td>
              <td className="px-3 py-1.5">
                <div className="flex items-center gap-2">
                  {sc.rooms != null ? (
                    <>
                      <button type="button" onClick={() => toggleScopeAll(sc)} data-testid={`ra-scope-all-${pid}-${sc.scope}`} data-open={scopeAllOpen(sc) ? "1" : "0"}
                              title={scopeAllOpen(sc) ? `Collapse ${sc.scope}` : `Expand ${sc.scope} down to its rooms`} className={TOGGLE}>
                        {loading && open ? <Loader2 className="h-3 w-3 animate-spin" /> : scopeAllOpen(sc) ? <ChevronsDownUp className="h-3 w-3" /> : <ChevronsUpDown className="h-3 w-3" />}
                      </button>
                      {sc.at_risk && <RiskBadge reasons={sc.risk_reasons} testId={`project-scope-risk-${pid}-${sc.scope}`} label={sc.scope} />}
                      <button type="button" onClick={() => toggle(keyOf(sc))} data-testid={`project-scope-open-${pid}-${sc.scope}`} data-open={open ? "1" : "0"}
                              title={open ? "Hide this scope's buildings, floors, areas and rooms" : "Show this scope's buildings, floors, areas and rooms"}
                              className={`text-[11px] font-semibold rounded border px-1.5 py-0.5 ${open ? "bg-gray-800 text-white border-gray-800 dark:bg-gray-200 dark:text-gray-900 dark:border-gray-200" : "text-gray-700 bg-gray-100 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700"}`}>
                        <span data-testid={`project-scope-label-${pid}-${sc.scope}`}>{sc.scope}</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-block w-5" />
                      {sc.at_risk && <RiskBadge reasons={sc.risk_reasons} testId={`project-scope-risk-${pid}-${sc.scope}`} label={sc.scope} />}
                      <span className="text-[11px] font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700"
                            data-testid={`project-scope-label-${pid}-${sc.scope}`}>{sc.scope}</span>
                    </>
                  )}
                  <LinkMark project={project} sc={sc} scopeRows={scopeRows} actor={actor} onChanged={onChanged} />
                  {sc.rooms != null && (
                    <span className={`text-[11px] tabular-nums ${done ? "text-emerald-700" : "text-gray-500"}`} data-testid={`project-scope-rooms-${pid}-${sc.scope}`}
                          title={`${sc.complete_rooms} of ${sc.rooms} rooms complete`}>
                      {done ? "complete" : `${sc.complete_rooms} of ${sc.rooms}`}
                    </span>
                  )}
                  {sc.hours_total > 0 && (
                    <span className="text-[11px] font-medium text-gray-700 tabular-nums" data-testid={`project-scope-hours-${pid}-${sc.scope}`}
                          title={`${sc.hours_left} of ${sc.hours_total} budgeted hours remain on this scope`}>
                      · {Math.round(sc.hours_left)} hrs remain
                    </span>
                  )}
                  <MobsBadge mobs={sc.mobs} testId={`project-scope-mobs-${pid}-${sc.scope}`} />
                  {sc.open_punch > 0 && (
                    <span className="text-[11px] font-semibold text-red-700 dark:text-red-400" data-testid={`project-scope-punch-${pid}-${sc.scope}`}
                          title={`${sc.open_punch} open punch item${sc.open_punch === 1 ? "" : "s"} on this scope`}>+ punch ({sc.open_punch})</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-1.5">
                {sc.rooms != null && <RequestPills project={project} sc={sc} onChanged={afterChange} onMob={(s) => onMob(s)} />}
              </td>
              <td colSpan={2}></td>
              <td className="px-3 py-1.5" data-testid={`project-scope-where-${pid}-${sc.scope}`}>
                {(() => {
                  const where = start.where || [];
                  if (!where.length) return <span className="text-[11px] text-gray-400">—</span>;
                  return (
                    <span className="text-[11px] text-gray-700" title={where.length > 1 ? `Areas starting ${fmtDMY(start.date)}:\n${where.join("\n")}` : undefined}>
                      {where[0]}
                    </span>
                  );
                })()}
              </td>
              <td className="px-3 py-1.5">
                {sc.rooms != null ? (
                  <FrsDateCell value={start.date || ""} locked={!!sc.start_locked} past={!!start.past && !sc.start_locked} multi={!sc.start_locked && !!start.multi} plain
                               onCommit={(v) => save({ level: "project", scope_type: sc.scope_type }, "start", v)}
                               testId={`project-scope-start-${pid}-${sc.scope}`} unlockLabel="Back to the job's date"
                               hint={sc.start_locked ? `Set on ${sc.scope} — every building, floor, area and room of it that is not set on its own follows`
                                 : start.past ? `Late — a scheduled card's start (${dateTitle(start.date)}). Pick a date to set the whole scope`
                                 : start.date ? `Next start ${dateTitle(start.date)}. Pick a date to set the whole scope` : "Set the scope's start"} />
                ) : <span className="text-xs text-gray-400">{done ? "—" : ""}</span>}
              </td>
              <td className={`px-3 py-1.5 text-[11px] tabular-nums whitespace-nowrap ${wk.tone}`} data-testid={`project-scope-weeks-${pid}-${sc.scope}`}>{wk.text || <span className="text-gray-400">—</span>}</td>
              <td className="px-3 py-1.5">
                <span className={`text-xs tabular-nums ${dateTone(sc.end_abs) || "text-gray-700"}`}
                      title={sc.end_abs ? `Completion ${dateTitle(sc.end_abs)} · calculated: start + ${Math.round(sc.hours_left)} remaining hours at the crew size` : ""}
                      data-testid={`project-scope-completion-${pid}-${sc.scope}`}>
                  {fmtDMY(sc.end_abs) || "—"}
                </span>
              </td>
              <td className="px-3 py-1.5 text-center">
                {sc.rooms != null && <MaterialChecks colour={sc.material} testId={`project-scope-material-${pid}-${sc.scope}`} label={sc.scope}
                                                     groups={[{ label: sc.scope, colour: sc.material, detail: sc.material_detail || {} }]} />}
              </td>
              <td className="px-3 py-1.5 text-center">
                {sc.rooms != null && <DocChecks checks={sc.checks} testId={`project-scope-doc-control-${pid}-${sc.scope}`} label={sc.scope} />}
              </td>
              <td colSpan={2}></td>
            </tr>
            {open && !data && (
              <tr data-testid={`ra-tree-loading-${pid}-${sc.scope}`}><td colSpan={14} className="px-3 py-1 text-[11px] text-gray-400"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" />Loading rooms…</td></tr>
            )}
            {open && nodes && nodes.length === 0 && (
              <tr data-testid={`ra-tree-empty-${pid}-${sc.scope}`}><td colSpan={14} className="px-3 py-1 text-[11px] text-gray-400">Every room of this scope is complete.</td></tr>
            )}
            {open && nodes && renderNodes(sc, nodes)}
          </React.Fragment>
        );
      })}
      {dialogs}
    </>
  );
}
